"use client";

import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store";
import { clearError } from "@/store/errorSlice";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCopy } from "@fortawesome/free-solid-svg-icons";
import { hexToRgba } from "@/utils/color";

// Extract style constants
const ERROR_TEXT_COLOR = "#b10c00";
const ERROR_BG_COLOR = "#ffe6f0";
const PREMIUM_LINK_COLOR = "#196ecf";

// Extract styles to reduce inline styling
const styles = {
  banner: (borderColor: string): React.CSSProperties => ({
    backgroundColor: ERROR_BG_COLOR,
    color: ERROR_TEXT_COLOR,
    padding: "12px 16px",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    position: "relative",
    borderColor,
    borderBottom: `1px solid ${borderColor}`,
  }),
  contentContainer: {
    width: "80%",
    maxWidth: "800px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  messageContainer: {
    flex: 1,
    textAlign: "center",
  },
  subject: {
    fontSize: "16px",
    fontWeight: 700,
  },
  subMessage: {
    fontSize: "14px",
    fontWeight: 500,
  },
  premiumLink: {
    color: PREMIUM_LINK_COLOR,
  },
  detailsLink: {
    color: PREMIUM_LINK_COLOR,
    cursor: "pointer",
    textDecoration: "underline",
    fontSize: "12px",
    fontWeight: "500",
  },
  detailsContainer: {
    width: "80%",
    maxWidth: "800px",
    marginTop: "8px",
    padding: "8px",
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: "4px",
    wordBreak: "break-word" as const,
    position: "relative",
  },
  copyContainer: {
    position: "absolute",
    top: "4px",
    right: "8px",
    cursor: "pointer",
    color: PREMIUM_LINK_COLOR,
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  closeIcon: (isHovering: boolean): React.CSSProperties => ({
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    marginLeft: "16px",
    border: `1px solid ${ERROR_TEXT_COLOR}`,
    backgroundColor: isHovering ? ERROR_TEXT_COLOR : "transparent",
    color: isHovering ? ERROR_BG_COLOR : ERROR_TEXT_COLOR,
    transition: "background-color 0.2s, color 0.2s",
  }),
};

const ErrorDisplay: React.FC = () => {
  const [isHovering, setIsHovering] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const dispatch = useDispatch();
  const error = useSelector((state: RootState) => state.error);

  // Return null early if no error message
  if (!error.message) return null;

  const handleDismiss = () => {
    dispatch(clearError());
  };

  const toggleDetails = () => {
    setShowDetails((prev) => !prev);
  };

  const handleCopy = () => {
    const errorDetails = [
      error.apiRoute && `API Route: ${error.apiRoute}`,
      `Message: ${error.message}`,
    ]
      .filter(Boolean)
      .join("\n");

    navigator.clipboard.writeText(errorDetails).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const borderColorWithOpacity = ERROR_TEXT_COLOR.startsWith("#")
    ? hexToRgba(ERROR_TEXT_COLOR, 0.25)
    : ERROR_TEXT_COLOR;

  return (
    <div style={styles.banner(borderColorWithOpacity)}>
      <div style={styles.contentContainer}>
        <div style={styles.messageContainer as React.CSSProperties}>
          <div style={styles.subject}>
            There appears to be an error loading your request.
          </div>
          <div style={styles.subMessage}>
            If the problem persists, contact the{" "}
            <Link href="/help/contact-premium" style={styles.premiumLink}>
              Premium program.
            </Link>
          </div>
        </div>
        <div
          onClick={handleDismiss}
          style={styles.closeIcon(isHovering)}
          title="Close"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <FontAwesomeIcon icon={faTimes} />
        </div>
      </div>
      <div style={styles.contentContainer}>
        <div style={{ flex: 1, textAlign: "right" }}>
          <a onClick={toggleDetails} style={styles.detailsLink}>
            {showDetails ? "Hide Details" : "Show Details"}
          </a>
        </div>
      </div>
      {showDetails && (
        <div style={styles.detailsContainer as React.CSSProperties}>
          <div
            style={styles.copyContainer as React.CSSProperties}
            onClick={handleCopy}
            title="Copy to Clipboard"
          >
            {copied ? (
              "Copied!"
            ) : (
              <>
                <FontAwesomeIcon icon={faCopy} />
                <span>Copy</span>
              </>
            )}
          </div>
          <pre style={{ marginTop: "1rem", whiteSpace: "pre-wrap", fontSize: "10px" }}>
            {error.apiRoute && `API Route: ${error.apiRoute}\n`}
            {`Message: ${error.message}`}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ErrorDisplay;
